import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, X, Play, Loader2, Package, Sparkles, Search, Filter, MessageCircle, User, Phone } from 'lucide-react';
import { getAllPublicKits } from '../api/kit.api';
import { PRODUCT_CATEGORIES } from '../data/productCategories';

const AiGalleryView = ({ setActiveView, showNotification }) => {
  const [kits, setKits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [selectedKit, setSelectedKit] = useState(null);
  const [searchInput, setSearchInput] = useState('Dress');
  const [searchQuery, setSearchQuery] = useState('Dress');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [failedImages, setFailedImages] = useState(new Set());
  const observerTarget = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Fetch kits with pagination
  const fetchKits = useCallback(async (page = 1, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const response = await getAllPublicKits(page, 24, searchQuery, selectedCategory);
      const { kits: fetchedKits, pagination } = response;

      if (append) {
        setKits(prev => [...prev, ...fetchedKits]);
      } else {
        setKits(fetchedKits);
        // Reset failed images when fetching new data
        setFailedImages(new Set());
      }

      setCurrentPage(pagination.currentPage);
      setHasNextPage(pagination.hasNextPage || false);
    } catch (error) {
      console.error('Error fetching kits:', error);
      if (showNotification) {
        showNotification('Failed to load designs. Please try again.', 'error');
      }
      if (!append) {
        setKits([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [showNotification, searchQuery, selectedCategory]);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Fetch kits when search query or category changes
  useEffect(() => {
    setCurrentPage(1);
    fetchKits(1, false);
  }, [searchQuery, selectedCategory, fetchKits]);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (!hasNextPage || isLoadingMore || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasNextPage && !isLoadingMore && !isLoading) {
          fetchKits(currentPage + 1, true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
      observer.disconnect();
    };
  }, [hasNextPage, isLoadingMore, isLoading, currentPage, fetchKits]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/75 backdrop-blur-xl border-b border-slate-200/70">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveView('home')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-700 hover:text-slate-900"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <div className="fd-brand-mark w-10 h-10 rounded-lg">
                <img src="/logo.png?v=20260711" alt="Fashdeck" />
              </div>
              <div>
                <h1 className="text-xl font-bold">AI Gallery</h1>
                <p className="text-xs text-slate-500">Generated Designs</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Search and Filter Section */}
      <div className="sticky top-[73px] z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200 py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Box */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search designs..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent shadow-sm"
              />
            </div>

            {/* Category Filter */}
            <div className="relative md:w-64">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent appearance-none cursor-pointer shadow-sm"
              >
                <option value="">All Categories</option>
                {PRODUCT_CATEGORIES.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters Button */}
            {(searchInput || selectedCategory) && (
              <button
                onClick={() => {
                  setSearchInput('');
                  setSelectedCategory('');
                  setCurrentPage(1);
                }}
                className="px-4 py-2.5 text-slate-600 hover:text-slate-900 transition-colors text-sm font-semibold"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {isLoading && kits.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
            <span className="ml-3 text-slate-600">Loading designs...</span>
          </div>
        ) : kits.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No designs available yet</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {kits.filter((kit) => {
                const kitImage = kit.generatedImageUrl || kit.productDetails?.image || kit.thumbnail;
                // Don't show kit if no image or if image failed to load
                return kitImage && !failedImages.has(kit._id);
              }).map((kit) => {
                const kitImage = kit.generatedImageUrl || kit.productDetails?.image || kit.thumbnail;

                return (
                  <div
                    key={kit._id}
                    className="group relative bg-white rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-all duration-300 border border-slate-200 hover:border-slate-300"
                    onClick={() => setSelectedKit(kit)}
                  >
                    <div className="aspect-[3/4] bg-slate-100 relative overflow-hidden">
                      <img
                        src={kitImage}
                        alt={kit.name || kit.productDetails?.name || 'Generated Design'}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        loading="lazy"
                        onError={() => {
                          // Mark this kit's image as failed
                          setFailedImages(prev => new Set([...prev, kit._id]));
                        }}
                      />
                      {kit.status === 'generated' && (
                        <div className="absolute top-2 right-2 bg-green-600 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-lg">
                          ✓
                        </div>
                      )}
                      {kit.videoUrl && (
                        <div className="absolute top-2 left-2 bg-slate-900 text-white p-1.5 rounded-full shadow-lg">
                          <Play size={12} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Infinite Scroll Trigger */}
            {hasNextPage && (
              <div
                ref={observerTarget}
                className="flex justify-center items-center py-8 min-h-[100px]"
              >
                {isLoadingMore ? (
                  <div className="flex items-center gap-2 text-violet-600">
                    <Loader2 size={24} className="animate-spin" />
                    <span className="text-sm font-medium">Loading more designs...</span>
                  </div>
                ) : (
                  <div className="h-20 w-full" />
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Kit Detail Modal */}
      {selectedKit && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setSelectedKit(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Fabric Details</h2>
                <button
                  onClick={() => setSelectedKit(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-700"
                >
                  <X size={24} className="text-slate-700" />
                </button>
              </div>

              {/* Fabric Image */}
              {(() => {
                const fabricImage = selectedKit.productDetails?.image || selectedKit.productDetails?.originalImage;
                if (!fabricImage) return null;
                return (
                  <div className="mb-6">
                    <img
                      src={fabricImage}
                      alt="Fabric"
                      className="w-full h-auto rounded-xl object-contain bg-slate-50 ring-1 ring-black/5"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                );
              })()}

              {/* WhatsApp Button */}
              {(() => {
                const fabricImage = selectedKit.productDetails?.image || selectedKit.productDetails?.originalImage;
                const sellerPhone = selectedKit.user?.phone;
                
                if (!sellerPhone) return null;

                const handleWhatsApp = () => {
                  const message = "Hey, I'm interested in this fabric";
                  const normalizedPhone = sellerPhone.replace(/\D/g, '');
                  const phoneWithCountryCode = normalizedPhone.startsWith('91') ? normalizedPhone : `91${normalizedPhone}`;
                  
                  // For WhatsApp web, we include the image URL in the message
                  // WhatsApp will automatically generate a preview if the URL is accessible
                  let whatsappMessage = message;
                  if (fabricImage) {
                    whatsappMessage = `${message}\n\n${fabricImage}`;
                  }
                  
                  const whatsappUrl = `https://wa.me/${phoneWithCountryCode}?text=${encodeURIComponent(whatsappMessage)}`;
                  window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
                };

                return (
                  <button
                    onClick={handleWhatsApp}
                    className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl font-semibold flex items-center justify-center gap-3 transition-all shadow-lg hover:shadow-xl"
                  >
                    <MessageCircle size={24} />
                    Connect with Vendor on WhatsApp
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiGalleryView;

